import React, { useEffect, useRef, useState } from "react";
import { X, Sparkles, Bell, Check } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../../redux/reducers/productSlice";
import "./latest.css";
import { adaptProductForCard } from "../utils";
import { ProductCard } from "./productCard";
import { addToCartAsync, removeFromCartAsync } from "../../redux/reducers/cart";
// These three always show, in this order, regardless of what's in the catalog.

// Sticky pills only apply at this size and below. Keep in sync with latest.css.
const SMALL_SCREEN_QUERY = "(max-width: 767px)";
const STATIC_FILTERS = ["For You", "New In", "Sale"];

// How "new" a product needs to be (in days since creation) to count as "New In"
// when the product has no explicit isNew flag.
const NEW_IN_WINDOW_DAYS = 30;

/**
 * Pulls a category label off a product.
 * ADAPT THIS: point it at whatever field adaptProductForCard actually returns
 * (e.g. p.category, p.categoryName, p.type, p.taxonomy?.name, etc).
 */
function getProductCategory(product) {
  const raw =
    product?.category ??
    product?.categoryName ??
    product?.subcategory ??
    product?.subCategory ??
    product?.type ??
    null;

  if (!raw) return null;
  if (typeof raw === "object") return raw.name ?? raw.slug ?? null;
  return raw;
}

/**
 * Decides if a product counts as "New In".
 * ADAPT THIS: if your API already sends an isNew/isNewArrival boolean, this
 * will pick it up automatically. Otherwise it falls back to a date check.
 */
function isNewProduct(product) {
  if (typeof product?.isNew === "boolean") return product.isNew;
  if (typeof product?.isNewArrival === "boolean") return product.isNewArrival;

  const created = product?.createdAt || product?.publishedAt;
  if (created) {
    const createdTime = new Date(created).getTime();
    if (Number.isNaN(createdTime)) {
      return false;
    }
    const ageInDays = (Date.now() - createdTime) / (1000 * 60 * 60 * 24);
    return ageInDays >= 0 && ageInDays <= NEW_IN_WINDOW_DAYS;
  }
  return false;
}

/**
 * Decides if a product counts as "Sale".
 * ADAPT THIS: if your API sends an explicit onSale/discountPercent field,
 * prefer that over the price comparison.
 */
function isOnSaleProduct(product) {
  if (typeof product?.onSale === "boolean") return product.onSale;
  return (
    typeof product?.originalPrice === "number" &&
    typeof product?.price === "number" &&
    product.originalPrice > product.price
  );
}

/**
 * Checks a product's sex/division field against the requested division.
 * Your raw product objects have a top-level `sex` field (e.g. "Men"), so
 * that's the primary thing checked here, with a couple of fallbacks in case
 * adaptProductForCard renames it.
 * Products with no sex/division info at all are kept visible rather than
 * hidden — flip the `if (!sex) return true;` line below to `return false;`
 * if you'd rather hide anything unclassified.
 */
function matchesDivision(product, division) {
  if (!division) return true;
  const sex = product?.sex ?? product?.division ?? product?.gender;
  if (!sex) return true;
  if (Array.isArray(sex)) {
    return sex.some((s) => String(s).toLowerCase() === division.toLowerCase());
  }
  return String(sex).toLowerCase() === division.toLowerCase();
}
const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];

// S < M < L < XL … ; numeric sizes (38, 40, 42) sort numerically after letters
const sizeRank = (size) => {
  const i = SIZE_ORDER.indexOf(String(size).toUpperCase());
  if (i >= 0) return i;
  const n = parseFloat(size);
  return Number.isNaN(n) ? 1000 : 100 + n;
};

function SizeSheet({ product, onClose }) {
  const [selected, setSelected] = useState(null);
  const { sizesAvailable = [], sizesNotify = [] } = product || {};

  // sort a copy by sizeRank; keep original order for ties/unknowns
  const sortSizes = (list) =>
    [...list].sort((a, b) => sizeRank(a) - sizeRank(b));

  const available = sortSizes(sizesAvailable);
  const notify = sortSizes(sizesNotify);

  function handlePick(size) {
    setSelected(size);
    setTimeout(onClose, 220);
  }

  return (
    <div className="stl-overlay" onClick={onClose}>
      <div className="stl-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="stl-sheet-handle" />
        <div className="stl-sheet-header">
          <h3>Select Size</h3>
          <button
            className="stl-sheet-close"
            aria-label="Close"
            type="button"
            onClick={onClose}
          >
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        {available.length > 0 && (
          <div className="stl-size-grid">
            {available.map((s) => (
              <button
                key={s}
                type="button"
                className={`stl-size-btn ${selected === s ? "selected" : ""}`}
                onClick={() => handlePick(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {notify.length > 0 && (
          <div className="stl-size-row-notify">
            {notify.map((s) => (
              <button
                key={s}
                type="button"
                className="stl-size-btn stl-size-btn--notify"
              >
                <Bell size={11} strokeWidth={2} />
                {s}
              </button>
            ))}
          </div>
        )}

        {available.length === 0 && notify.length === 0 && (
          <p className="stl-size-empty">No sizes available</p>
        )}
      </div>
    </div>
  );
}
export default function ShopTheLatest({ division }) {
  const dispatch = useDispatch();
  const { cart } = useSelector((state) => state.cart);
  const { products, loadingList, error } = useSelector(
    (state) => state.products,
  );

  const [activeFilter, setActiveFilter] = useState("For You");
  const [sizeProduct, setSizeProduct] = useState(null);
  const headingRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);
  const sectionRef = useRef(null);
  const pillsWrapRef = useRef(null);
  const [pillsHeight, setPillsHeight] = useState(0);

  /* fetch on mount, and re-fetch if the division changes */
  useEffect(() => {
    dispatch(fetchProducts({ page: 1, limit: 20, sex: division }));
  }, [dispatch, division]);

  /* sticky filter observer */
  /* stick the pills on small screens only */
  useEffect(() => {
    const section = sectionRef.current;
    const heading = headingRef.current;
    const wrap = pillsWrapRef.current;
    if (!section || !heading || !wrap) return;

    const mq = window.matchMedia(SMALL_SCREEN_QUERY);
    let ticking = false;
    let naturalHeight = 0;

    const update = () => {
      ticking = false;

      const header = document.querySelector(".fn-header");
      const headerH = header ? header.offsetHeight : 0;
      document.documentElement.style.setProperty(
        "--fn-header-height",
        `${headerH}px`,
      );

      // measure the pills in normal flow so the spacer can hold their place
      if (!wrap.classList.contains("is-stuck")) {
        naturalHeight = wrap.offsetHeight;
        setPillsHeight(naturalHeight);
      }

      const headingBottom = heading.getBoundingClientRect().bottom;
      const sectionBottom = section.getBoundingClientRect().bottom;

      setIsStuck(
        headingBottom <= headerH && sectionBottom > headerH + naturalHeight,
      );
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    // attach scroll/resize listeners only while on a small screen
    const sync = () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);

      if (mq.matches) {
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        update();
      } else {
        setIsStuck(false); // larger screen: never stuck
        setPillsHeight(0);
      }
    };

    sync();
    mq.addEventListener("change", sync);

    return () => {
      mq.removeEventListener("change", sync);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const adaptedProducts = React.useMemo(() => {
    if (!Array.isArray(products)) return [];
    return products.map((raw) => {
      const adapted = adaptProductForCard(raw);
      return {
        ...adapted,
        category: adapted.category ?? raw.category ?? null,
        subcategory: adapted.subcategory ?? raw.subcategory ?? null,
        sex: adapted.sex ?? raw.sex ?? raw.division ?? raw.gender ?? null,
        // Always take this straight from raw — don't merge with whatever
        // adaptProductForCard may have done to it, since a mangled non-null
        // value would silently win over the ?? fallback.
        createdAt: raw.createdAt ?? null,
      };
    });
  }, [products]);

  /* client-side safety net: only keep products matching the requested
     division, in case the server-side "sex" param above isn't being
     honored (or isn't implemented) yet */
  const divisionProducts = React.useMemo(
    () => adaptedProducts.filter((p) => matchesDivision(p, division)),
    [adaptedProducts, division],
  );

  /* build the filter pill list: fixed filters + whatever categories exist */
  const categoryFilters = React.useMemo(() => {
    const categories = new Set();
    divisionProducts.forEach((p) => {
      const cat = getProductCategory(p);
      if (cat) categories.add(cat);
    });
    return Array.from(categories).sort((a, b) => a.localeCompare(b));
  }, [divisionProducts]);

  const filters = React.useMemo(
    () => [...STATIC_FILTERS, ...categoryFilters],
    [categoryFilters],
  );

  /* if the active filter disappears (e.g. that category has no more items
     after a refetch), fall back to "For You" instead of showing a dead state */
  useEffect(() => {
    if (!filters.includes(activeFilter)) {
      setActiveFilter("For You");
    }
  }, [filters, activeFilter]);

  /* actually apply the selected filter, on top of the division-scoped list */
  const filteredProducts = React.useMemo(() => {
    switch (activeFilter) {
      case "For You":
        return divisionProducts;
      case "New In":
        return divisionProducts.filter(isNewProduct);
      case "Sale":
        return divisionProducts.filter(isOnSaleProduct);
      default:
        return divisionProducts.filter(
          (p) => getProductCategory(p) === activeFilter,
        );
    }
  }, [divisionProducts, activeFilter]);

  const isSizeInCart = (productId, colorId, size) =>
    cart.some(
      (item) =>
        item.productId === productId &&
        item.color?._id === colorId &&
        item.size === size,
    );

  const handleSheetSizeClick = (product, sizeObj) => {
    const colorId = product.variant?.color?._id;
    if (!colorId) return;

    const inCart = isSizeInCart(product.id, colorId, sizeObj.size);

    if (inCart) {
      dispatch(removeFromCartAsync(product.id, colorId, sizeObj.size));
    } else {
      dispatch(
        addToCartAsync({
          productId: product.id,
          name: product.title,
          slug: product.slug,
          brand: product.brand,
          basePrice: product.price,
          originalPrice: product.originalPrice,
          currency: product.currency,
          image: product.image,
          color: product.variant.color,
          size: sizeObj.size,
          sku: sizeObj.sku,
          stock: sizeObj.stock,
          quantity: 1,
        }),
      );
    }
  };

  return (
    <section ref={sectionRef} className="stl-wrap">
      <h2 ref={headingRef} className="stl-heading">
        Shop The Latest
      </h2>

      <div
        ref={pillsWrapRef}
        className={`stl-pills-wrap ${isStuck ? "is-stuck" : ""}`}
      >
        <div className="stl-pills">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              className={`stl-pill ${activeFilter === f ? "active" : ""}`}
              onClick={() => setActiveFilter(f)}
            >
              {f === "For You" && <Sparkles size={14} strokeWidth={2} />}
              {f}
            </button>
          ))}
        </div>
      </div>

      <div
        className="stl-pills-spacer"
        style={{ height: isStuck ? pillsHeight : 0 }}
      />
      {loadingList && <p className="stl-status">Loading…</p>}
      {error && <p className="stl-status stl-status--error">Error: {error}</p>}

      {!loadingList && !error && filteredProducts.length > 0 && (
        <div className="stl-grid">
          {filteredProducts.map((p) => (
            <ProductCard key={p.id} {...p} onPickSize={setSizeProduct} />
          ))}
        </div>
      )}

      {!loadingList && !error && filteredProducts.length === 0 && (
        <p className="stl-status">No products in “{activeFilter}” right now.</p>
      )}

      {sizeProduct && (
        <div
          className="size-sheet-overlay"
          onClick={() => setSizeProduct(null)}
        >
          <div className="size-sheet" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="size-sheet-header">
              <img
                src={sizeProduct.image}
                alt={sizeProduct.title}
                className="size-sheet-img"
              />
              <div className="size-sheet-meta">
                <p className="size-sheet-title">{sizeProduct.title}</p>
                <p className="size-sheet-price">
                  {sizeProduct.currency?.toLowerCase?.() === "kes"
                    ? "Kes"
                    : sizeProduct.currency}{" "}
                  {sizeProduct.price?.toLocaleString?.()}
                </p>
              </div>
              <button
                className="size-sheet-close"
                onClick={() => setSizeProduct(null)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="size-sheet-body">
              <span className="size-sheet-label">
                Color: <strong>{sizeProduct.variant?.color?.name}</strong>
              </span>

              <span className="size-sheet-label">Select Size</span>
              <div className="size-sheet-grid">
                {sizeProduct.variant?.sizes?.map((sizeObj) => {
                  const colorId = sizeProduct.variant?.color?._id;
                  const inCart = isSizeInCart(
                    sizeProduct.id,
                    colorId,
                    sizeObj.size,
                  );
                  const available = sizeObj.stock > 0;

                  return (
                    <button
                      key={sizeObj.size}
                      className={`size-sheet-btn ${
                        inCart ? "size-sheet-btn--incart" : ""
                      } ${!available ? "size-sheet-btn--disabled" : ""}`}
                      disabled={!available}
                      onClick={() => handleSheetSizeClick(sizeProduct, sizeObj)}
                    >
                      {inCart ? (
                        <Check size={14} strokeWidth={2.5} />
                      ) : (
                        sizeObj.size
                      )}
                    </button>
                  );
                })}
              </div>

              {sizeProduct.variant?.sizes?.every((s) => s.stock <= 0) && (
                <p className="size-sheet-oos">All sizes out of stock</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
